import { useCallback, useEffect, useRef, useState } from "react";
import { ResetFiltersButton } from "@/components/common/ResetFiltersButton";
import { useSearchParams } from "react-router-dom";
import { PageHeader } from "@/components/common/PageHeader";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { DRSummary } from "@/components/dr/DRSummary";
import { DROverview } from "@/components/dr/DROverview";
import { DecisionBoard } from "@/components/dr/DecisionBoard";
import { RiskBoard } from "@/components/dr/RiskBoard";
import { ReviewsHistory } from "@/components/dr/ReviewsHistory";

const DR_TABS = ["overview", "decisions", "risks", "reviews"];

export default function DecisionsRisks() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [bump, setBump] = useState(0);
  const refresh = useCallback(() => setBump((b) => b + 1), []);
  const decRef = useRef(null); const riskRef = useRef(null);

  const tabParam = searchParams.get("tab");
  const tab = DR_TABS.includes(tabParam) ? tabParam : "overview";
  const filter = searchParams.get("filter");
  const severity = searchParams.get("severity");

  const changeTab = (v) => { const p = new URLSearchParams(searchParams); p.set("tab", v); ["filter", "severity", "decision", "risk"].forEach((k) => p.delete(k)); setSearchParams(p); };
  const clearFilter = () => { const p = new URLSearchParams(searchParams); ["filter", "severity"].forEach((k) => p.delete(k)); setSearchParams(p); };
  const consumeParam = useCallback((k) => { const p = new URLSearchParams(searchParams); p.delete(k); setSearchParams(p, { replace: true }); }, [searchParams, setSearchParams]);

  const openDecision = (id) => { const p = new URLSearchParams(searchParams); p.set("tab", "decisions"); p.set("decision", id); setSearchParams(p); };
  const openRisk = (id) => { const p = new URLSearchParams(searchParams); p.set("tab", "risks"); p.set("risk", id); setSearchParams(p); };

  // Direct decision/risk deep links via imperative handles once the board is mounted.
  useEffect(() => {
    const did = searchParams.get("decision");
    if (tab === "decisions" && did) { const h = setTimeout(() => decRef.current?.openDetailById(did, () => consumeParam("decision")), 80); return () => clearTimeout(h); }
  }, [tab, searchParams, consumeParam]);
  useEffect(() => {
    const rid = searchParams.get("risk");
    if (tab === "risks" && rid) { const h = setTimeout(() => riskRef.current?.openDetailById(rid, () => consumeParam("risk")), 80); return () => clearTimeout(h); }
  }, [tab, searchParams, consumeParam]);

  return (
    <>
      <PageHeader
        eyebrow="War Room"
        title="Decisions & Risks"
        description="A disciplined record of what has been decided and why, and a clear view of the risks that matter most — who owns them, how serious they are, and what is being done."
      />

      <div className="mb-8"><DRSummary refreshKey={bump} /></div>

      <ResetFiltersButton /><Tabs value={tab} onValueChange={changeTab} className="w-full">
        <TabsList data-testid="dr-tabs" className="mb-6 flex h-auto flex-wrap justify-start gap-1 rounded-full border border-[#E4E9F3] bg-white p-1.5 shadow-[0_2px_12px_rgba(20,36,95,0.05)]">
          {[["overview", "Overview"], ["decisions", "Decisions"], ["risks", "Risks"], ["reviews", "Reviews & History"]].map(([v, l]) => (
            <TabsTrigger key={v} value={v} data-testid={`tab-${v}`} className="rounded-full px-4 py-1.5 text-sm font-medium text-[#5A6B8C] transition-colors data-[state=active]:text-white data-[state=active]:shadow-sm data-[state=active]:[background-color:var(--accent-primary)]">{l}</TabsTrigger>
          ))}
        </TabsList>

        <TabsContent value="overview"><DROverview refreshKey={bump} onOpenDecision={openDecision} onOpenRisk={openRisk} /></TabsContent>
        <TabsContent value="decisions"><DecisionBoard ref={decRef} onChange={refresh} initialFilter={filter} onClearFilter={clearFilter} /></TabsContent>
        <TabsContent value="risks"><RiskBoard ref={riskRef} onChange={refresh} initialFilter={filter} initialSeverity={severity} onClearFilter={clearFilter} /></TabsContent>
        <TabsContent value="reviews"><ReviewsHistory refreshKey={bump} initialFilter={filter} onClearFilter={clearFilter} /></TabsContent>
      </Tabs>
    </>
  );
}
